import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, TargetIcon, LayoutIcon, FileTextIcon, ZapIcon, MapIcon, SparklesIcon } from 'lucide-react';
export function OfferSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const inclusions = [{
    text: 'una guía principal paso a paso,',
    icon: BookOpenIcon
  }, {
    text: 'una explicación clara del modelo,',
    icon: TargetIcon
  }, {
    text: 'estrategias de IA aplicadas a servicios visuales,',
    icon: SparklesIcon
  }, {
    text: 'estructura para organizar tu oferta,',
    icon: LayoutIcon
  }, {
    text: 'recursos de apoyo para simplificar el proceso,',
    icon: FileTextIcon
  }, {
    text: 'materiales pensados para ayudarte a pasar a la acción más rápido,',
    icon: ZapIcon
  }, {
    text: 'y acompañamiento estratégico en forma de ruta clara para que no te quedes estancada.',
    icon: MapIcon
  }];
  const notNeeded = ['ser experta en diseño,', 'tener experiencia en tecnología,', 'una computadora costosa,', 'grandes inversiones,', 'ni meses de preparación antes de dar el primer paso.'];
  return <section className="bg-white py-20 md:py-32" data-id="element-175">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-176">
        {/* What's inside */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="mb-24" data-id="element-177">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-navy mb-6 text-center" data-id="element-178">
            ¿Qué encontrarás dentro?
          </motion.h2>
          <motion.p variants={itemVariants} className="text-lg text-slate-600 mb-10 text-center max-w-2xl mx-auto" data-id="element-179">
            Dentro de IA Express 2026 encontrarás una estructura práctica y
            enfocada en acción para ayudarte a construir tu mini-negocio visual
            sin perderte en teoría innecesaria.
          </motion.p>

          <motion.div variants={itemVariants} className="bg-navy rounded-3xl p-8 md:p-12 shadow-xl text-white" data-id="element-180">
            <p className="text-gold font-medium mb-6 text-lg" data-id="element-181">Incluye:</p>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-182">
              {inclusions.map((item, index) => {
              const Icon = item.icon;
              return <motion.div key={index} variants={itemVariants} className="flex items-start gap-4 bg-white/5 p-4 rounded-xl border border-white/10" data-id="element-183">
                    <div className="bg-gold/20 p-2 rounded-lg shrink-0" data-id="element-184">
                      <Icon className="w-5 h-5 text-gold" data-id="element-185" />
                    </div>
                    <span className="text-slate-200 leading-snug" data-id="element-186">
                      {item.text}
                    </span>
                  </motion.div>;
            })}
            </div>
            <motion.p variants={itemVariants} className="mt-8 text-center text-gold italic font-serif text-lg" data-id="element-187">
              Todo está diseñado para ayudarte a avanzar con claridad, no para
              confundirte con tecnicismos.
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Market Opportunity */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="mb-24" data-id="element-188">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-navy mb-8" data-id="element-189">
            Por qué esta oportunidad tiene sentido hoy
          </motion.h2>

          <div className="space-y-6 text-lg text-slate-700 leading-relaxed" data-id="element-190">
            <motion.p variants={itemVariants} data-id="element-191">
              Hoy muchísimos negocios y emprendedores necesitan comunicar mejor
              lo que ofrecen.
            </motion.p>
            <motion.p variants={itemVariants} data-id="element-192">
              Necesitan verse más profesionales. Necesitan ahorrar tiempo.
              Necesitan mejorar su presencia. Necesitan soluciones simples y
              ejecutables.
            </motion.p>
            <motion.p variants={itemVariants} className="font-medium text-navy text-xl" data-id="element-193">
              Y ahí es donde entra una gran oportunidad.
            </motion.p>
            <motion.p variants={itemVariants} data-id="element-194">
              Cuando tú aprendes a crear una propuesta visual útil, clara y
              actual, dejas de ver la IA como algo abstracto… y empiezas a verla
              como una herramienta para generar valor real.
            </motion.p>
            <motion.div variants={itemVariants} className="border-l-4 border-gold pl-6 py-2 my-8" data-id="element-195">
              <p className="font-medium text-navy mb-2" data-id="element-196">
                No estás entrando a una moda vacía. Estás entrando a una
                necesidad concreta del mercado con una propuesta simple y
                actual.
              </p>
              <p data-id="element-197">
                Eso vuelve esta oferta mucho más poderosa. Porque no parte de
                una fantasía. Parte de una necesidad que ya existe.
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* No complications */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="bg-cream rounded-3xl p-8 md:p-12 shadow-sm border border-gold/20 mb-24" data-id="element-198">
          <motion.h2 variants={itemVariants} className="text-2xl md:text-3xl font-bold text-navy mb-6" data-id="element-199">
            Lo mejor de todo: no necesitas complicarte
          </motion.h2>
          <motion.p variants={itemVariants} className="text-lg text-slate-600 mb-6" data-id="element-200">
            Para comenzar con IA Express 2026, no necesitas:
          </motion.p>

          <ul className="space-y-3 mb-8" data-id="element-201">
            {notNeeded.map((item, index) => <motion.li key={index} variants={itemVariants} className="flex items-center gap-3 text-lg text-slate-700" data-id="element-202">
                <div className="w-2 h-2 rounded-full bg-gold shrink-0" data-id="element-203"></div>
                {item}
              </motion.li>)}
          </ul>

          <motion.p variants={itemVariants} className="text-xl font-serif font-bold text-navy" data-id="element-204">
            Lo que necesitas es una decisión. Y una guía correcta.
          </motion.p>
        </motion.div>

        {/* Urgency */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="text-center max-w-2xl mx-auto" data-id="element-205">
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-navy mb-8" data-id="element-206">
            Si hoy sigues esperando "el momento ideal"…
          </motion.h2>

          <div className="space-y-6 text-lg text-slate-700 leading-relaxed" data-id="element-207">
            <motion.p variants={itemVariants} className="font-medium" data-id="element-208">
              Déjame decirte algo con total honestidad: Ese momento ideal casi
              nunca llega.
            </motion.p>
            <motion.p variants={itemVariants} data-id="element-209">
              Porque siempre habrá cansancio. Siempre habrá dudas. Siempre habrá
              miedo. Siempre habrá una excusa para dejarlo para después.
            </motion.p>
            <motion.p variants={itemVariants} className="text-rose-600 font-medium" data-id="element-210">
              Pero también llega un punto en el que seguir postergándolo duele
              más que empezar.
            </motion.p>
            <motion.p variants={itemVariants} data-id="element-211">
              Y quizá ese punto para ti es hoy.
            </motion.p>
            <motion.p variants={itemVariants} data-id="element-212">
              Quizá hoy ya no quieres solo consumir contenido. Quizá hoy quieres
              una ruta. Una estructura. Una posibilidad real de construir algo
              que sí se adapte a tu vida actual.
            </motion.p>
            <motion.p variants={itemVariants} className="text-xl font-serif font-bold text-gold pt-4" data-id="element-213">
              Entonces IA Express 2026 puede ser ese primer paso.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>;
}